import React from 'react';
import { Box, Container, Heading, Text, VStack, HStack, SimpleGrid, Icon, Flex, Badge, Image } from '@chakra-ui/react';
import { FiMapPin, FiClock, FiWifi, FiCoffee, FiMonitor } from 'react-icons/fi';
import { FaCar, FaParking } from 'react-icons/fa';
import { formatState, formatCityState } from '../../utils/formatLocation';

const amenityIcons = { 'Free WiFi': FiWifi, 'Morning Coffee': FiCoffee, 'Widescreen TV': FiMonitor, 'Drive-Through': FaCar, 'Free Parking': FaParking, 'Comfortable Seating': FiMonitor, 'Vending Machines': FiCoffee, 'Card & Coin Machines': FiMonitor };

// Format a US phone number as (XXX) XXX-XXXX. Strips non-digits and an optional
// leading country code "1". Falls back to the original value if it isn't a
// standard 10-digit US number, so international/odd formats are left as-is.
const formatPhone = (raw) => {
    if (!raw) return '';
    let digits = String(raw).replace(/\D/g, '');
    if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1);
    if (digits.length === 10) {
        return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    return String(raw);
};

export default function SiteLocation({ config }) {
    const sc = config?.siteContent || {};
    const themeColor = sc.themeColor || 'blue';
    const hours = sc.hours || [];
    const hoursNote = sc.hoursNote || '';
    const amenities = sc.amenities || [];
    const locationPhotos = sc.locationPhotos || [];

    return (
        <Box id="location" py={{ base: 16, md: 20 }} bg="gray.50">
            <Container maxW="1200px">
                <VStack spacing={4} textAlign="center" mb={12}>
                    {/* Pickup/delivery-only tenants (no public street address) get a
                        service-area framing; tenants with a storefront address keep
                        the classic "Visit Us / Our Location" headings. */}
                    {/* Eyebrow differs from the heading so the two never echo the
                        same words (was previously "Our Service Area" in both). */}
                    <Text fontSize="sm" fontWeight="600" color={`${themeColor}.500`} textTransform="uppercase" letterSpacing="wide">
                        {sc.address ? 'Visit Us' : 'Where We Serve'}
                    </Text>
                    <Heading fontSize={{ base: '2xl', md: '4xl' }} color="gray.800">
                        {sc.address ? 'Our Location' : 'Our Service Area'}
                    </Heading>
                    {/* Tenant-configured free-text service-area paragraph (schedule /
                        coverage) when set; otherwise the default one-liner. */}
                    {(sc.serviceAreaNote || '').trim() ? (
                        <Text fontSize={{ base: 'md', md: 'lg' }} color="gray.500" maxW="640px">
                            {sc.serviceAreaNote}
                        </Text>
                    ) : (
                        <Text fontSize={{ base: 'md', md: 'lg' }} color="gray.500" maxW="600px">
                            Proudly serving {sc.city || 'your area'} and surrounding communities.
                        </Text>
                    )}
                </VStack>

                <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={8}>
                    {/* Map — only shown when a public street address is available.
                        Home-based operators (address hidden) get a service-area panel instead. */}
                    {sc.address ? (
                        <Box as="a" href={`https://www.google.com/maps/dir/?api=1&destination=${sc.mapsQuery || ''}`} target="_blank" rel="noopener noreferrer"
                            borderRadius="2xl" overflow="hidden" boxShadow="md" minH="300px" display="block" position="relative" bg={`${themeColor}.50`}
                            _hover={{ boxShadow: 'lg', transform: 'translateY(-2px)' }} transition="all 0.2s">
                            <img src={`https://maps.googleapis.com/maps/api/staticmap?center=${sc.mapsQuery || ''}&zoom=15&size=600x400&markers=color:${themeColor === 'green' ? 'green' : 'blue'}%7C${sc.mapsQuery || ''}&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ''}`}
                                alt="Location" style={{ width: '100%', height: '100%', objectFit: 'cover', minHeight: '300px' }} />
                            <Box position="absolute" bottom="0" left="0" right="0" bg="rgba(0,0,0,0.6)" color="white" py={2} px={4} textAlign="center">
                                <Text fontSize="sm" fontWeight="600">Tap for Directions →</Text>
                            </Box>
                        </Box>
                    ) : (
                        <Box borderRadius="2xl" boxShadow="md" minH="300px" display="flex" flexDirection="column" alignItems="center" justifyContent="center"
                            bg={`${themeColor}.50`} p={8} textAlign="center">
                            <Icon as={FiMapPin} color={`${themeColor}.500`} boxSize={10} mb={4} />
                            <Text fontWeight="700" color="gray.800" fontSize="lg">{sc.pickupDeliveryLabel || 'Pickup & Delivery'}</Text>
                            <Text fontSize="md" color="gray.600" mt={1}>
                                Serving {formatCityState(sc.city, sc.state) || 'your area'} and surrounding areas
                            </Text>
                        </Box>
                    )}

                    {/* Info */}
                    <VStack spacing={6} align="stretch">
                        <Box bg="white" borderRadius="2xl" p={6} boxShadow="sm" border="1px solid" borderColor="gray.100">
                            <HStack spacing={3} mb={3}>
                                <Box bg={`${themeColor}.50`} borderRadius="lg" p={2}><Icon as={FiMapPin} color={`${themeColor}.500`} boxSize={5} /></Box>
                                <Text fontWeight="700" color="gray.800">{sc.address ? 'Address' : 'Service Area'}</Text>
                            </HStack>
                            <Text fontSize="md" color="gray.600">
                                {sc.address
                                    ? <>{sc.address}<br />{sc.city}, {formatState(sc.state)} {sc.zip}</>
                                    : <>Pickup &amp; delivery across {formatCityState(sc.city, sc.state) || 'your area'}</>}
                            </Text>
                            {sc.phone && <Text fontSize="sm" color="gray.500" mt={2}>📞 {formatPhone(sc.phone)}</Text>}
                        </Box>

                        <Box bg="white" borderRadius="2xl" p={6} boxShadow="sm" border="1px solid" borderColor="gray.100">
                            <HStack spacing={3} mb={3}>
                                <Box bg={`${themeColor}.50`} borderRadius="lg" p={2}><Icon as={FiClock} color={`${themeColor}.500`} boxSize={5} /></Box>
                                <Text fontWeight="700" color="gray.800">Hours</Text>
                                <Badge colorScheme="green" borderRadius="full" fontSize="xs">Open</Badge>
                            </HStack>
                            <VStack spacing={2} align="stretch">
                                {hoursNote ? (
                                    <Text fontSize="sm" color="gray.600">{hoursNote}</Text>
                                ) : (
                                    hours.map((h, i) => (
                                        <Flex key={i} justify="space-between">
                                            <Text fontSize="sm" color="gray.600">{h.label}</Text>
                                            <Text fontSize="sm" fontWeight="600" color="gray.700">{h.time}</Text>
                                        </Flex>
                                    ))
                                )}
                            </VStack>
                        </Box>

                        {/* Amenities are optional and only relevant for walk-in
                            locations. Pickup-and-delivery tenants have none, so we
                            hide the box entirely rather than showing an empty one. */}
                        {amenities.length > 0 && (
                            <Box bg="white" borderRadius="2xl" p={6} boxShadow="sm" border="1px solid" borderColor="gray.100">
                                <Text fontWeight="700" color="gray.800" mb={3}>Amenities</Text>
                                <Flex gap={3} flexWrap="wrap">
                                    {amenities.map((a) => (
                                        <HStack key={a} bg="gray.50" borderRadius="full" px={3} py={1.5} spacing={2}>
                                            <Icon as={amenityIcons[a] || FiCoffee} color={`${themeColor}.400`} boxSize={4} />
                                            <Text fontSize="xs" fontWeight="500" color="gray.600">{a}</Text>
                                        </HStack>
                                    ))}
                                </Flex>
                            </Box>
                        )}
                    </VStack>
                </SimpleGrid>

                {/* Photo gallery of the physical location — walk-in laundromats.
                    Hidden entirely for pickup-and-delivery-only tenants (no photos). */}
                {locationPhotos.length > 0 && (
                    <Box mt={12}>
                        <Heading fontSize={{ base: 'xl', md: '2xl' }} color="gray.800" textAlign="center" mb={6}>
                            Inside Our Store
                        </Heading>
                        <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} spacing={4}>
                            {locationPhotos.map((src, i) => (
                                <Box key={i} borderRadius="xl" overflow="hidden" boxShadow="sm"
                                    border="1px solid" borderColor="gray.100">
                                    <Image src={src} alt={`${sc.laundryName || 'Our store'} photo ${i + 1}`}
                                        objectFit="cover" w="100%" h={{ base: '200px', md: '220px' }}
                                        loading="lazy" />
                                </Box>
                            ))}
                        </SimpleGrid>
                    </Box>
                )}
            </Container>
        </Box>
    );
}
